import React, { useEffect, useRef, useState, useCallback } from "react";

import { motion, AnimatePresence } from "framer-motion";

import apiService from "../services/api";
import { debugLog } from "../utils/debugLog";
import watchProgressUtils from "../utils/watchProgressUtils";

import SubtitlesModal from "./SubtitlesModal";

const PlyrVideoPlayer = ({
  isOpen,
  onClose,
  streamUrl,
  title,
  poster,
  posterPath,
  backdropPath,
  mediaId,
  mediaType,
  seriesId,
  episodeId,
  episodeInfo,
}) => {
  // Samsung parity: backend needs title + poster + thumbnail in the
  // /v2/recently-watched PATCH body to populate /v2/home → continueWatching.
  const buildProgressMeta = useCallback(
    () => ({
      title: title || null,
      posterUrl: posterPath || poster || null,
      thumbnailUrl: backdropPath || null,
      streamUrl: streamUrl || null,
      episodeId: episodeId || null,
      episodeInfo: episodeInfo || null,
    }),
    [title, posterPath, poster, backdropPath, streamUrl, episodeId, episodeInfo]
  );

  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const hlsRef = useRef(null);
  const initializePlayerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mkvWarning, setMkvWarning] = useState(null);
  const [showTitle, setShowTitle] = useState(true);
  const [mouseActive, setMouseActive] = useState(false);
  const [showSubtitlesModal, setShowSubtitlesModal] = useState(false);
  const [subtitles, setSubtitles] = useState([]);
  const [selectedSubtitle, setSelectedSubtitle] = useState(null);
  const [subtitlesLoading, setSubtitlesLoading] = useState(false);
  const [hasNativeSubtitles, setHasNativeSubtitles] = useState(false);
  const [nativeSubtitlesEnabled, setNativeSubtitlesEnabled] = useState(false);
  const initAttempts = useRef(0);
  const mouseMoveTimer = useRef(null);
  const subtitleTrackRef = useRef(null);
  // Guards the CC-from-settings auto-load so it fires once per media (prevents
  // a re-render loop while the async subtitle load is in flight).
  const autoCaptionLoadedRef = useRef(false);

  // Get closed captions setting from localStorage
  const [captionsEnabled, setCaptionsEnabled] = useState(false);

  // Function to update captions from settings
  const updateCaptionsFromSettings = () => {
    const savedSettings = localStorage.getItem("webPlayerSettings");

    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        const newCaptionsState = settings.closedCaptions || false;

        setCaptionsEnabled(newCaptionsState);

        // Only update Plyr if it exists, is ready, and has the toggleCaptions method
        if (
          playerRef.current &&
          playerRef.current.ready &&
          playerRef.current.toggleCaptions
        ) {
          try {
            playerRef.current.toggleCaptions(newCaptionsState);
          } catch (e) {
            console.warn("Could not toggle captions:", e);
          }
        }
      } catch (e) {
        console.error("Error parsing settings:", e);
        setCaptionsEnabled(false);
      }
    }
  };

  useEffect(() => {
    // Only update captions settings from storage, don't try to toggle player captions yet
    const savedSettings = localStorage.getItem("webPlayerSettings");

    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        const newCaptionsState = settings.closedCaptions || false;

        setCaptionsEnabled(newCaptionsState);
      } catch (e) {
        console.error("Error parsing settings:", e);
        setCaptionsEnabled(false);
      }
    }
  }, [isOpen]);

  // Listen for storage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "webPlayerSettings") {
        updateCaptionsFromSettings();
      }
    };

    const handleSettingsUpdate = () => {
      updateCaptionsFromSettings();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("webPlayerSettingsUpdated", handleSettingsUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "webPlayerSettingsUpdated",
        handleSettingsUpdate
      );
    };
  }, []);

  // Auto-hide MKV warning and title after 10 seconds
  useEffect(() => {
    if (mkvWarning) {
      const timer = setTimeout(() => {
        setMkvWarning(null);
        setShowTitle(false); // Hide title when warning fades
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [mkvWarning]);

  // Handle mouse movement to show/hide title
  useEffect(() => {
    if (!isOpen) return;

    const handleMouseMove = () => {
      setMouseActive(true);
      setShowTitle(true); // Show title on mouse movement

      // Clear existing timer
      if (mouseMoveTimer.current) {
        clearTimeout(mouseMoveTimer.current);
      }

      // Hide title after 3 seconds of no mouse movement
      mouseMoveTimer.current = setTimeout(() => {
        setMouseActive(false);
        if (!mkvWarning) {
          // Only hide if warning is not showing
          setShowTitle(false);
        }
      }, 3000);
    };

    const handleMouseLeave = () => {
      // Hide title when mouse leaves the player
      if (mouseMoveTimer.current) {
        clearTimeout(mouseMoveTimer.current);
      }
      setMouseActive(false);
      if (!mkvWarning) {
        setShowTitle(false);
      }
    };

    // Add event listeners to the container
    const container = containerRef.current;

    if (container) {
      container.addEventListener("mousemove", handleMouseMove);
      container.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      if (container) {
        container.removeEventListener("mousemove", handleMouseMove);
        container.removeEventListener("mouseleave", handleMouseLeave);
      }
      if (mouseMoveTimer.current) {
        clearTimeout(mouseMoveTimer.current);
      }
    };
  }, [isOpen, mkvWarning]);

  // Load external scripts
  useEffect(() => {
    // Load Plyr CSS
    if (!document.querySelector('link[href*="plyr.css"]')) {
      const link = document.createElement("link");

      link.rel = "stylesheet";
      link.href = "https://cdn.plyr.io/3.7.8/plyr.css";
      document.head.appendChild(link);
    }

    // Load Plyr JS
    if (!window.Plyr && !document.querySelector('script[src*="plyr.js"]')) {
      const script = document.createElement("script");

      script.src = "https://cdn.plyr.io/3.7.8/plyr.polyfilled.js";
      script.async = true;
      document.body.appendChild(script);
    }

    // Load HLS.js for streaming support
    if (!window.Hls && !document.querySelector('script[src*="hls.js"]')) {
      const hlsScript = document.createElement("script");

      hlsScript.src = "https://cdn.jsdelivr.net/npm/hls.js@1/dist/hls.min.js";
      hlsScript.async = true;
      document.body.appendChild(hlsScript);
    }
  }, []);

  // Function to convert URL to M3U8 format and match protocol to page
  const ensureM3U8Format = (url) => {
    if (!url) return null;

    // Match stream protocol to page protocol to avoid mixed content
    const pageProtocol = window.location.protocol; // 'http:' or 'https:'
    let processedUrl = url;

    // Force stream to use same protocol as page
    if (pageProtocol === "https:") {
      processedUrl = url.replace(/^http:/, "https:");
    }

    // If already M3U8, return with matched protocol
    if (processedUrl.includes(".m3u8") || processedUrl.includes(".m3u")) {
      return processedUrl;
    }

    // Xtream Codes URL patterns
    const streamIdPattern =
      /^(https?:\/\/[^/]+)\/(?!live\/)([^/]+)\/([^/]+)\/(\d+)$/;
    const match = processedUrl.match(streamIdPattern);

    if (match) {
      const [, domain, username, password, streamId] = match;

      return `${domain}/live/${username}/${password}/${streamId}.m3u8`;
    }

    // If URL already has /live, just add .m3u8
    const withLivePattern = /\/live\/.*\/(\d+)$/;

    if (withLivePattern.test(processedUrl)) {
      return `${processedUrl}.m3u8`;
    }

    return processedUrl;
  };

  // Fetch subtitles from API
  const fetchSubtitles = useCallback(async () => {
    if (subtitles.length > 0) return;

    setSubtitlesLoading(true);
    try {
      let response;

      if (mediaType === "movie") {
        if (!mediaId) {
          setSubtitlesLoading(false);

          return;
        }
        response = await apiService.webPlayerGetMovieSubtitles(mediaId);
      } else if (
        mediaType === "series" ||
        mediaType === "episode" ||
        mediaType === "tv"
      ) {
        // For series, use seriesId and episodeId
        if (!seriesId || !episodeId) {
          debugLog(
            "Subtitles: Missing seriesId or episodeId for series subtitles"
          );
          setSubtitlesLoading(false);

          return;
        }
        response = await apiService.webPlayerGetSeriesSubtitles(
          seriesId,
          episodeId
        );
      } else {
        setSubtitlesLoading(false);

        return;
      }

      if (response?.data && Array.isArray(response.data)) {
        setSubtitles(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch subtitles:", err);
    } finally {
      setSubtitlesLoading(false);
    }
  }, [mediaId, mediaType, seriesId, episodeId, subtitles.length]);

  // Check for native subtitles in HLS stream
  const checkForNativeSubtitles = useCallback(() => {
    if (hlsRef.current) {
      const { subtitleTracks } = hlsRef.current;

      setHasNativeSubtitles(subtitleTracks && subtitleTracks.length > 0);
    } else if (videoRef.current) {
      const tracks = videoRef.current.textTracks;
      let hasNative = false;

      for (let i = 0; i < tracks.length; i++) {
        if (tracks[i].kind === "subtitles" || tracks[i].kind === "captions") {
          hasNative = true;
          break;
        }
      }
      setHasNativeSubtitles(hasNative);
    }
  }, []);

  // Store subtitle blob URL ref (declared here, used later)
  const subtitleBlobUrlRef = useRef(null);

  // Remove external subtitle (cleanup only, no reload)
  const removeExternalSubtitle = useCallback(() => {
    // Revoke blob URL to prevent memory leak
    if (subtitleBlobUrlRef.current) {
      URL.revokeObjectURL(subtitleBlobUrlRef.current);
      debugLog("Revoked blob URL:", subtitleBlobUrlRef.current);
      subtitleBlobUrlRef.current = null;
    }

    subtitleTrackRef.current = null;

    // Remove from localStorage
    const storageKey = `subtitle_${mediaId}_${mediaType}`;

    localStorage.removeItem(storageKey);

    setSelectedSubtitle(null);
  }, [mediaId, mediaType]);

  // Toggle native subtitles
  const toggleNativeSubtitles = useCallback(() => {
    const newState = !nativeSubtitlesEnabled;

    setNativeSubtitlesEnabled(newState);
    debugLog("Toggling native subtitles:", newState);

    // If enabling native subs, disable external subs
    if (newState && selectedSubtitle) {
      removeExternalSubtitle();
    }

    // Toggle HLS native subtitles
    if (hlsRef.current) {
      const { subtitleTracks } = hlsRef.current;

      debugLog("HLS subtitle tracks available:", subtitleTracks);
      if (subtitleTracks && subtitleTracks.length > 0) {
        hlsRef.current.subtitleTrack = newState ? 0 : -1;
        hlsRef.current.subtitleDisplay = newState;
        debugLog(
          "Set HLS subtitleTrack to:",
          newState ? 0 : -1,
          "subtitleDisplay:",
          newState
        );
      }
    }

    // Toggle HTML5 text tracks
    if (videoRef.current) {
      const tracks = videoRef.current.textTracks;

      debugLog("HTML5 text tracks:", tracks.length);
      for (let i = 0; i < tracks.length; i++) {
        if (tracks[i].kind === "subtitles" || tracks[i].kind === "captions") {
          tracks[i].mode = newState ? "showing" : "hidden";
          debugLog(
            "Set track",
            i,
            tracks[i].label,
            "to",
            newState ? "showing" : "hidden"
          );
        }
      }
    }

    // Also try to toggle Plyr captions if available
    if (playerRef.current && playerRef.current.toggleCaptions) {
      try {
        playerRef.current.toggleCaptions(newState);
      } catch (e) {
        console.warn("Could not toggle Plyr captions:", e);
      }
    }
  }, [nativeSubtitlesEnabled, selectedSubtitle, removeExternalSubtitle]);

  // Convert SRT to VTT format (HTML5 video only supports WebVTT)
  const convertSrtToVtt = (srtContent) => {
    // Add VTT header
    let vtt = "WEBVTT\n\n";

    // Replace SRT timing format (00:00:00,000) with VTT format (00:00:00.000)
    // and handle the cue blocks
    const srtCues = srtContent
      .replace(/\r\n/g, "\n") // Normalize line endings
      .replace(/\r/g, "\n")
      .trim()
      .split(/\n\n+/); // Split by empty lines

    srtCues.forEach((cue) => {
      const lines = cue.split("\n");

      if (lines.length >= 2) {
        // Skip the cue number (first line), get timing (second line)
        const timingLine = lines.find((line) => line.includes("-->"));

        if (timingLine) {
          // Convert comma to period in timestamps
          const vttTiming = timingLine.replace(/,/g, ".");
          // Get text lines (everything after timing)
          const timingIndex = lines.indexOf(timingLine);
          const textLines = lines.slice(timingIndex + 1).join("\n");

          if (textLines.trim()) {
            vtt += `${vttTiming}\n${textLines}\n\n`;
          }
        }
      }
    });

    return vtt;
  };

  // Add subtitle track to video (without reloading)
  const addSubtitleTrack = useCallback((blobUrl, subtitle) => {
    try {
      if (!videoRef.current) {
        console.error("Cannot add subtitle: missing video element");
        setIsLoading(false);

        return;
      }

      const video = videoRef.current;

      // Remove existing subtitle tracks we added
      try {
        const existingTracks = video.querySelectorAll(
          'track[data-external="true"]'
        );

        existingTracks.forEach((t) => t.remove());
      } catch (e) {
        console.error("Error removing existing tracks:", e);
      }

      if (blobUrl) {
        // Add new subtitle track
        const track = document.createElement("track");

        track.kind = "captions";
        track.label = subtitle?.name || subtitle?.language || "Subtitles";
        track.srclang = subtitle?.language || "en";
        track.src = blobUrl;
        track.default = true;
        track.dataset.external = "true";
        video.appendChild(track);
        subtitleTrackRef.current = track;

        // Wait for track to load then enable it
        track.addEventListener("load", () => {
          debugLog("Subtitle track loaded, enabling...");
          enableSubtitleTrack(video);
        });

        // Also try after a delay as fallback
        setTimeout(() => {
          enableSubtitleTrack(video);
          setIsLoading(false);
        }, 300);
      } else {
        // No subtitle - disable all tracks
        if (video.textTracks) {
          for (let i = 0; i < video.textTracks.length; i++) {
            video.textTracks[i].mode = "hidden";
          }
        }
        setIsLoading(false);
      }
    } catch (e) {
      console.error("Error adding subtitle track:", e);
      setIsLoading(false);
    }
  }, []);

  // Helper to enable subtitle track
  const enableSubtitleTrack = (video) => {
    try {
      if (video.textTracks && video.textTracks.length > 0) {
        // First hide all tracks
        for (let i = 0; i < video.textTracks.length; i++) {
          video.textTracks[i].mode = "hidden";
        }
        // Then show our external track (last one added)
        const lastTrack = video.textTracks[video.textTracks.length - 1];

        if (lastTrack) {
          lastTrack.mode = "showing";
          debugLog("Enabled subtitle track:", lastTrack.label);
        }
      }
      // Also try Plyr captions
      if (playerRef.current && playerRef.current.captions) {
        playerRef.current.captions.active = true;
        playerRef.current.captions.currentTrack = video.textTracks.length - 1;
      }
    } catch (e) {
      console.error("Error enabling subtitle track:", e);
    }
  };

  // Load external subtitle
  const loadExternalSubtitle = useCallback(
    async (subtitle) => {
      if (!videoRef.current || !subtitle) return;

      debugLog("Loading subtitle:", subtitle.url);
      setIsLoading(true);

      // Disable native subtitles if enabled
      if (nativeSubtitlesEnabled) {
        setNativeSubtitlesEnabled(false);
        // Hide HLS native subtitle tracks
        if (hlsRef.current) {
          hlsRef.current.subtitleTrack = -1;
          hlsRef.current.subtitleDisplay = false;
        }
      }

      try {
        // Fetch the subtitle file
        const response = await fetch(subtitle.url);

        if (!response.ok) {
          throw new Error(`Failed to fetch subtitle: ${response.status}`);
        }

        let subtitleContent = await response.text();

        debugLog("Subtitle content fetched, length:", subtitleContent.length);

        // Check if it's already VTT or needs conversion from SRT
        if (!subtitleContent.trim().startsWith("WEBVTT")) {
          debugLog("Converting SRT to VTT...");
          subtitleContent = convertSrtToVtt(subtitleContent);
        }

        // Clean up previous blob URL
        if (subtitleBlobUrlRef.current) {
          URL.revokeObjectURL(subtitleBlobUrlRef.current);
        }

        // Create a blob URL for the VTT content
        const blob = new Blob([subtitleContent], { type: "text/vtt" });
        const blobUrl = URL.createObjectURL(blob);

        subtitleBlobUrlRef.current = blobUrl;
        debugLog("Created blob URL for subtitle:", blobUrl);

        setSelectedSubtitle(subtitle);

        // Save preference to localStorage
        const storageKey = `subtitle_${mediaId}_${mediaType}`;

        localStorage.setItem(storageKey, JSON.stringify(subtitle));

        // Add subtitle track to video (no reload needed)
        addSubtitleTrack(blobUrl, subtitle);
      } catch (error) {
        console.error("Error loading subtitle:", error);
        setIsLoading(false);
      }
    },
    [nativeSubtitlesEnabled, mediaId, mediaType, addSubtitleTrack]
  );

  // Handle subtitle selection from modal
  const handleSubtitleSelect = useCallback(
    (subtitle) => {
      if (subtitle === null) {
        // User wants to disable subtitles
        removeExternalSubtitle();
        // Remove any external tracks from video
        if (videoRef.current) {
          const existingTracks = videoRef.current.querySelectorAll(
            'track[data-external="true"]'
          );

          existingTracks.forEach((t) => t.remove());
          // Hide all text tracks
          if (videoRef.current.textTracks) {
            for (let i = 0; i < videoRef.current.textTracks.length; i++) {
              videoRef.current.textTracks[i].mode = "hidden";
            }
          }
        }
      } else if (selectedSubtitle?.url === subtitle.url) {
        // Same subtitle selected, toggle off
        removeExternalSubtitle();
        // Remove external tracks
        if (videoRef.current) {
          const existingTracks = videoRef.current.querySelectorAll(
            'track[data-external="true"]'
          );

          existingTracks.forEach((t) => t.remove());
          if (videoRef.current.textTracks) {
            for (let i = 0; i < videoRef.current.textTracks.length; i++) {
              videoRef.current.textTracks[i].mode = "hidden";
            }
          }
        }
      } else {
        // New subtitle selected
        loadExternalSubtitle(subtitle);
      }
      setShowSubtitlesModal(false);
    },
    [selectedSubtitle, loadExternalSubtitle, removeExternalSubtitle]
  );

  // Open subtitles modal and fetch if needed
  const openSubtitlesModal = useCallback(() => {
    setShowSubtitlesModal(true);
    fetchSubtitles();
    checkForNativeSubtitles();
  }, [fetchSubtitles, checkForNativeSubtitles]);

  // Restore saved subtitle preference
  useEffect(() => {
    if (!isOpen || !mediaId || !mediaType) return;

    const storageKey = `subtitle_${mediaId}_${mediaType}`;
    const savedSubtitle = localStorage.getItem(storageKey);

    if (savedSubtitle) {
      try {
        const subtitle = JSON.parse(savedSubtitle);

        // Will load after player is ready
        setSelectedSubtitle(subtitle);
      } catch (e) {
        console.error("Error parsing saved subtitle:", e);
      }
    }
  }, [isOpen, mediaId, mediaType]);

  // Load saved subtitle after player is ready
  useEffect(() => {
    if (selectedSubtitle && videoRef.current && !subtitleTrackRef.current) {
      loadExternalSubtitle(selectedSubtitle);
    }
  }, [selectedSubtitle, loadExternalSubtitle]);

  // Reset the one-shot CC auto-load guard when the media or open-state changes.
  useEffect(() => {
    autoCaptionLoadedRef.current = false;
  }, [mediaId, mediaType, isOpen]);

  // When Closed Captions is enabled in settings, fetch subtitles up front so
  // there's a track to show (no need to open the subtitle menu first).
  useEffect(() => {
    if (isOpen && captionsEnabled) {
      fetchSubtitles();
    }
  }, [isOpen, captionsEnabled, fetchSubtitles]);

  // ...then auto-show a default track (prefer English) so CC appears on load.
  // autoCaptionLoadedRef makes this fire ONCE per media: loadExternalSubtitle
  // sets selectedSubtitle only after an async fetch, so without the ref the
  // effect would re-fire (reloading the player) before the guard catches up.
  useEffect(() => {
    if (
      !captionsEnabled ||
      selectedSubtitle ||
      autoCaptionLoadedRef.current ||
      subtitles.length === 0 ||
      !videoRef.current
    ) {
      return;
    }

    autoCaptionLoadedRef.current = true;

    const preferred =
      subtitles.find((s) =>
        String(s.language || s.lang || "")
          .toLowerCase()
          .startsWith("en")
      ) || subtitles[0];

    loadExternalSubtitle(preferred);
  }, [captionsEnabled, subtitles, selectedSubtitle, loadExternalSubtitle]);

  // Initialize player
  const initializePlayer = () => {
    if (!isOpen || !videoRef.current) return;

    if (!streamUrl) {
      console.error("PlyrVideoPlayer: No stream URL provided");
      setError("No stream URL provided");
      setIsLoading(false);

      return;
    }

    initAttempts.current++;

    // Check if libraries are loaded
    if (!window.Plyr || !window.Hls) {
      if (initAttempts.current < 50) {
        // Max 5 seconds wait
        setTimeout(() => initializePlayerRef.current?.(), 100);
      } else {
        setError("Failed to load video player libraries");
        setIsLoading(false);
      }

      return;
    }

    const video = videoRef.current;

    const streamM3U8Url = ensureM3U8Format(streamUrl);

    debugLog("PlyrVideoPlayer: Initializing with URL:", streamM3U8Url);
    debugLog("PlyrVideoPlayer: Title:", title || "No title");
    debugLog("PlyrVideoPlayer: Poster:", poster || "No poster");

    const isHLS =
      streamM3U8Url &&
      (streamM3U8Url.includes(".m3u8") || streamM3U8Url.includes(".m3u"));

    // Initialize HLS if needed
    if (isHLS && window.Hls.isSupported()) {
      debugLog("Using HLS.js for streaming");

      // Destroy existing HLS instance if any
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }

      hlsRef.current = new window.Hls({
        debug: false,
        enableWorker: true,
        backBufferLength: 90,
        maxBufferLength: 30,
        maxMaxBufferLength: 600,
        maxBufferSize: 60 * 1000 * 1000,
        maxBufferHole: 0.5,
        highBufferWatchdogPeriod: 2,
        nudgeOffset: 0.1,
        nudgeMaxRetry: 10,
        maxFragLookUpTolerance: 0.25,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 12,
        liveDurationInfinity: true,
        enableWebVTT: true,
        enableCEA708Captions: true,
        stretchShortVideoTrack: false,
        forceKeyFrameOnDiscontinuity: true,
        abrEwmaFastLive: 3,
        abrEwmaSlowLive: 9,
        abrEwmaFastVoD: 3,
        abrEwmaSlowVoD: 9,
        abrEwmaDefaultEstimate: 500000,
        abrBandWidthFactor: 0.95,
        abrBandWidthUpFactor: 0.7,
        abrMaxWithRealBitrate: false,
        maxStarvationDelay: 4,
        maxLoadingDelay: 4,
        minAutoBitrate: 0,
        emeEnabled: false,
        widevineLicenseUrl: undefined,
        licenseXhrSetup: undefined,
        manifestLoadingTimeOut: 10000,
        manifestLoadingMaxRetry: 1,
        manifestLoadingRetryDelay: 1000,
        manifestLoadingMaxRetryTimeout: 64000,
        startLevel: -1,
        levelLoadingTimeOut: 10000,
        levelLoadingMaxRetry: 4,
        levelLoadingRetryDelay: 1000,
        levelLoadingMaxRetryTimeout: 64000,
        fragLoadingTimeOut: 20000,
        fragLoadingMaxRetry: 6,
        fragLoadingRetryDelay: 1000,
        fragLoadingMaxRetryTimeout: 64000,
        startFragPrefetch: false,
        fpsDroppedMonitoringPeriod: 5000,
        fpsDroppedMonitoringThreshold: 0.2,
        appendErrorMaxRetry: 3,
        testBandwidth: true,
        progressive: true,
        lowLatencyMode: false,
      });

      hlsRef.current.loadSource(streamM3U8Url);
      hlsRef.current.attachMedia(video);

      hlsRef.current.on(window.Hls.Events.MANIFEST_PARSED, () => {
        debugLog("HLS manifest loaded");

        // Check for native subtitle tracks in the HLS stream
        if (hlsRef.current) {
          const { subtitleTracks } = hlsRef.current;

          debugLog("HLS subtitle tracks:", subtitleTracks);
          if (subtitleTracks && subtitleTracks.length > 0) {
            setHasNativeSubtitles(true);
            debugLog("Native subtitles detected:", subtitleTracks.length);
          }
        }

        try {
          initPlyr();
          video.play().catch((e) => {
            debugLog("Autoplay prevented:", e);
          });
        } catch (error) {
          console.error("Error during HLS manifest parsed handler:", error);
          setError("Failed to initialize player");
          setIsLoading(false);
        }
      });

      // Listen for subtitle tracks being updated
      hlsRef.current.on(window.Hls.Events.SUBTITLE_TRACKS_UPDATED, () => {
        if (hlsRef.current) {
          const { subtitleTracks } = hlsRef.current;

          if (subtitleTracks && subtitleTracks.length > 0) {
            setHasNativeSubtitles(true);
            debugLog(
              "Subtitle tracks updated, native subtitles:",
              subtitleTracks.length
            );
          }
        }
      });

      hlsRef.current.on(window.Hls.Events.ERROR, (event, data) => {
        console.error("HLS error:", data);
        if (data.fatal) {
          switch (data.type) {
            case window.Hls.ErrorTypes.NETWORK_ERROR:
              if (
                data.details === window.Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
                data.details === window.Hls.ErrorDetails.MANIFEST_PARSING_ERROR
              ) {
                setError("Failed to load stream. Please check the stream URL.");
                setIsLoading(false);
              } else {
                hlsRef.current.startLoad();
              }
              break;
            case window.Hls.ErrorTypes.MEDIA_ERROR:
              hlsRef.current.recoverMediaError();
              break;
            default:
              setError("Failed to load stream");
              setIsLoading(false);
              break;
          }
        }
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl") && isHLS) {
      // Native HLS support (Safari)
      debugLog("Using native HLS support");
      video.src = streamM3U8Url;
      initPlyr();
    } else {
      // Direct playback
      debugLog("Direct playback");

      // Check if it's an MKV file and show warning
      const isMKV = streamUrl && streamUrl.toLowerCase().includes(".mkv");

      if (isMKV) {
        debugLog("MKV file detected, using native browser playback");

        // Set up event listener for audio codec warning
        video.addEventListener("loadedmetadata", () => {
          // Check if audio is playing
          const hasAudio =
            video.mozHasAudio ||
            video.webkitAudioDecodedByteCount > 0 ||
            video.audioTracks?.length > 0;

          if (!hasAudio) {
            console.warn(
              "No audio detected - likely unsupported codec (AC3/DTS)"
            );
            setMkvWarning(
              "Audio codec not supported. Video playing without sound."
            );
          } else {
            debugLog("MKV playing with audio support");
          }
        });
      }

      video.src = streamM3U8Url;
      initPlyr();
    }

    function initPlyr() {
      try {
        // Destroy existing player if any
        if (playerRef.current) {
          try {
            playerRef.current.destroy();
          } catch (e) {
            console.error("Error destroying previous player:", e);
          }
          playerRef.current = null;
        }

        // Ensure video element exists and is ready
        if (!video || !video.parentNode) {
          console.error("Video element not ready for Plyr initialization");

          return;
        }

        // Small delay to ensure DOM is ready
        setTimeout(() => {
          if (!playerRef.current && video && video.parentNode) {
            // Create Plyr instance with optimized settings
            playerRef.current = new window.Plyr(video, {
              controls: [
                "play-large",
                "play",
                "progress",
                "current-time",
                "mute",
                "volume",
                "captions",
                "settings",
                "pip",
                "airplay",
                "fullscreen",
              ],
              settings: ["captions", "quality", "speed"],
              autoplay: true,
              hideControls: true,
              keyboard: { focused: true, global: true },
              tooltips: { controls: true, seek: true },
              fullscreen: {
                enabled: true,
                fallback: true,
                iosNative: true,
              },
              storage: { enabled: true, key: "plyr-video-player" },
              ratio: "16:9",
              clickToPlay: true,
              invertTime: true,
              captions: {
                active: captionsEnabled,
                language: "auto",
                update: true,
              },
              seekTime: 10,
              speed: {
                selected: 1,
                options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
              },
              quality: {
                default: "auto",
                options: ["auto"],
                forced: false,
                onChange: null,
              },
              i18n: {
                restart: "Restart",
                rewind: "Rewind {seektime}s",
                play: "Play",
                pause: "Pause",
                fastForward: "Forward {seektime}s",
                seek: "Seek",
                seekLabel: "{currentTime} of {duration}",
                played: "Played",
                buffered: "Buffered",
                currentTime: "Current time",
                duration: "Duration",
                volume: "Volume",
                mute: "Mute",
                unmute: "Unmute",
                enableCaptions: "Enable captions",
                disableCaptions: "Disable captions",
                download: "Download",
                enterFullscreen: "Enter fullscreen",
                exitFullscreen: "Exit fullscreen",
                frameTitle: "Player for {title}",
                captions: "Captions",
                settings: "Settings",
                pip: "PIP",
                menuBack: "Go back to previous menu",
                speed: "Speed",
                normal: "Normal",
                quality: "Quality",
                loop: "Loop",
                start: "Start",
                end: "End",
                all: "All",
                reset: "Reset",
                disabled: "Disabled",
                enabled: "Enabled",
                advertisement: "Ad",
                qualityBadge: {
                  2160: "4K",
                  1440: "HD",
                  1080: "HD",
                  720: "HD",
                  576: "SD",
                  480: "SD",
                },
              },
            });

            // Track if we've already seeked to resume position
            let hasResumed = false;

            // Add event listeners
            playerRef.current.on("ready", () => {
              debugLog("Plyr ready");
              // Don't hide loader yet - wait for canplay or playing event
              setError(null);

              // Apply caption settings after ready
              setTimeout(() => {
                if (playerRef.current && playerRef.current.toggleCaptions) {
                  try {
                    playerRef.current.toggleCaptions(captionsEnabled);
                  } catch (e) {
                    console.warn("Could not set initial captions state:", e);
                  }
                }
              }, 100);

              // Try to play
              if (video.src) {
                playerRef.current.play().catch((e) => {
                  console.error("Autoplay failed:", e);
                  setIsLoading(false);
                });
              }
            });

            // Handle resume position after video metadata is loaded (works better for HLS)
            video.addEventListener("loadedmetadata", () => {
              if (hasResumed) return;

              if (mediaId && mediaType) {
                debugLog(
                  "PlyrVideoPlayer: Video metadata loaded, checking resume position for:",
                  mediaId,
                  mediaType
                );

                const resumePosition = watchProgressUtils.getResumePosition(
                  mediaId,
                  mediaType
                );

                if (resumePosition && resumePosition > 0) {
                  debugLog(
                    "PlyrVideoPlayer: Resuming from position:",
                    resumePosition,
                    "seconds (",
                    Math.floor(resumePosition / 60),
                    "minutes",
                    Math.floor(resumePosition % 60),
                    "seconds)"
                  );
                  // For HLS, wait a bit then seek
                  setTimeout(() => {
                    if (video && !hasResumed) {
                      video.currentTime = resumePosition;
                      hasResumed = true;
                      debugLog("PlyrVideoPlayer: Seeked to", resumePosition);
                    }
                  }, 500);
                } else {
                  debugLog(
                    "PlyrVideoPlayer: No valid resume position found, starting from beginning"
                  );
                }
              }
            });

            // Hide loader when video is actually ready to play
            playerRef.current.on("playing", () => {
              debugLog("Video is playing");
              setIsLoading(false);

              // Start progress tracking when video starts playing
              if (mediaId && mediaType) {
                watchProgressUtils.startProgressTracking(
                  playerRef.current,
                  mediaId,
                  mediaType,
                  {},
                  buildProgressMeta()
                );
              }
            });

            playerRef.current.on("canplay", () => {
              debugLog("Video can play");
              setIsLoading(false);
            });

            playerRef.current.on("error", (event) => {
              console.error("Player error:", event);

              // Check if it's likely a codec issue
              const isMKV =
                streamUrl && streamUrl.toLowerCase().includes(".mkv");

              if (isMKV) {
                if (!error) {
                  // Don't overwrite existing error
                  setError(
                    "MKV audio codec not supported. The server needs to provide HLS streams or compatible formats."
                  );
                }
              } else {
                setError("Failed to play video");
              }
              setIsLoading(false);
            });

            playerRef.current.on("ended", () => {
              if (mediaId && mediaType && playerRef.current) {
                watchProgressUtils.savePlayerProgress(
                  playerRef.current,
                  mediaId,
                  mediaType,
                  buildProgressMeta()
                );
              }
              onClose();
            });

            playerRef.current.on("pause", () => {
              if (mediaId && mediaType && playerRef.current) {
                watchProgressUtils.savePlayerProgress(
                  playerRef.current,
                  mediaId,
                  mediaType,
                  buildProgressMeta()
                );
              }
            });

            playerRef.current.on("loadedmetadata", () => {
              if (video.duration === Infinity) {
                debugLog("Live stream detected");
              }

              // Check for audio tracks
              const { audioTracks } = video;

              if (audioTracks && audioTracks.length === 0) {
                console.warn("No audio tracks detected in video");
                const isMKV =
                  streamUrl && streamUrl.toLowerCase().includes(".mkv");

                if (isMKV) {
                  console.error(
                    "MKV file has incompatible audio codec. Common culprits: AC3, DTS, TrueHD"
                  );
                  // Show warning to user
                  if (!error) {
                    setError(
                      "Warning: Audio codec not supported. Video playing without sound."
                    );
                  }
                }
              }
            });
          }
        }, 50); // Small delay to ensure DOM is ready
      } catch (error) {
        console.error("Error initializing Plyr:", error);
        setError("Failed to initialize video player");
        setIsLoading(false);
      }
    }
  };

  initializePlayerRef.current = initializePlayer;

  // Main initialization effect
  useEffect(() => {
    const video = videoRef.current;

    if (isOpen && streamUrl) {
      // Only initialize if not already initialized
      if (!playerRef.current) {
        initAttempts.current = 0;
        setIsLoading(true);
        setError(null);
        initializePlayerRef.current?.();
      }
    }

    // Cleanup function - runs on unmount or when deps change
    return () => {
      // Clean up any pending initialization attempts
      initAttempts.current = 0;

      // Destroy HLS instance to stop any ongoing network requests
      if (hlsRef.current) {
        try {
          hlsRef.current.destroy();
        } catch (e) {
          console.error("Error destroying HLS on cleanup:", e);
        }
        hlsRef.current = null;
      }

      // Destroy player instance
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.error("Error destroying player on cleanup:", e);
        }
        playerRef.current = null;
      }

      // Stop video
      if (video) {
        try {
          video.pause();
          video.removeAttribute("src");
          video.load();
        } catch (e) {
          // Ignore errors
        }
      }
    };
  }, [isOpen, streamUrl]);

  // Cleanup effect when closing
  useEffect(() => {
    if (!isOpen) {
      // Save progress before cleanup
      if (mediaId && mediaType && playerRef.current) {
        watchProgressUtils.savePlayerProgress(
          playerRef.current,
          mediaId,
          mediaType,
          buildProgressMeta()
        );
      }

      // Stop progress tracking
      watchProgressUtils.stopProgressTracking();

      // Clear home page cache so it fetches fresh Continue Watching data
      sessionStorage.removeItem("home_continue_watching");
      sessionStorage.removeItem("home_carousel");

      // Small delay to let animations finish before cleanup
      const cleanupTimer = setTimeout(() => {
        // Cleanup when modal is closed
        if (playerRef.current) {
          try {
            // Ensure player is properly destroyed
            playerRef.current.destroy();
          } catch (e) {
            console.error("Error destroying player on cleanup:", e);
          }
          playerRef.current = null;
        }
        if (hlsRef.current) {
          try {
            hlsRef.current.destroy();
          } catch (e) {
            console.error("Error destroying HLS on cleanup:", e);
          }
          hlsRef.current = null;
        }
        // Reset video element
        if (videoRef.current) {
          videoRef.current.pause();
          videoRef.current.removeAttribute("src");
          videoRef.current.load();
          videoRef.current.style.display = "block";
        }

        // Reset subtitle state
        subtitleTrackRef.current = null;
        setSubtitles([]);
        setSelectedSubtitle(null);
        setHasNativeSubtitles(false);
        setNativeSubtitlesEnabled(false);
        setShowSubtitlesModal(false);
      }, 100);

      return () => clearTimeout(cleanupTimer);
    }
  }, [isOpen, mediaId, mediaType, buildProgressMeta]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-[100000] bg-black/95 flex items-center justify-center"
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          animate={{ scale: 1 }}
          className="relative w-full h-full max-w-[1152px] max-h-[648px] mx-auto flex items-center justify-center"
          exit={{ scale: 0.9 }}
          initial={{ scale: 0.9 }}
          ref={containerRef}
          transition={{ duration: 0.3 }}
        >
          {/* Video Container - Strict 16:9 aspect ratio */}
          <div
            className={`relative w-full h-full flex items-center justify-center ${!mouseActive && !showTitle ? "cursor-none" : "cursor-auto"}`}
            style={{ maxWidth: "calc(100vh * 16 / 9)" }}
          >
            <div
              className="relative w-full bg-black rounded-lg overflow-hidden shadow-2xl"
              style={{ aspectRatio: "16/9" }}
            >
              {/* Top right buttons with animation */}
              <AnimatePresence>
                {showTitle && (
                  <motion.div
                    animate={{ opacity: 1, scale: 1 }}
                    className="absolute top-4 right-4 z-50 flex items-center gap-2"
                    exit={{ opacity: 0, scale: 0.8 }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Subtitles button - shown for movies and series */}
                    {(mediaType === "movie" ||
                      ((mediaType === "series" ||
                        mediaType === "tv" ||
                        mediaType === "episode") &&
                        seriesId &&
                        episodeId)) && (
                      <button
                        aria-label="Subtitles"
                        className={`w-12 h-12 flex items-center justify-center rounded-full transition-colors duration-200 group ${
                          selectedSubtitle || nativeSubtitlesEnabled
                            ? "bg-purple-600/80 hover:bg-purple-600"
                            : "bg-black/50 hover:bg-black/70"
                        }`}
                        onClick={openSubtitlesModal}
                      >
                        <i
                          className={`fas fa-closed-captioning text-xl ${
                            selectedSubtitle || nativeSubtitlesEnabled
                              ? "text-white"
                              : "text-white/80 group-hover:text-white"
                          } group-hover:scale-110 transition-transform`}
                        />
                      </button>
                    )}
                    {/* Close button */}
                    <button
                      aria-label="Close player"
                      className="w-12 h-12 flex items-center justify-center bg-black/50 hover:bg-black/70 rounded-full transition-colors duration-200 group"
                      onClick={onClose}
                    >
                      <svg
                        className="w-6 h-6 text-white group-hover:scale-110 transition-transform"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M6 18L18 6M6 6l12 12"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                        />
                      </svg>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Title overlay with animation */}
              <AnimatePresence>
                {title && showTitle && (
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute top-4 left-4 z-40 max-w-[70%]"
                    exit={{ opacity: 0, y: -10 }}
                    initial={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-white drop-shadow-lg line-clamp-2">
                      {title}
                    </h2>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Loading indicator with purple theme */}
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center z-30 bg-black/70">
                  <div className="flex flex-col items-center gap-4">
                    <div className="relative">
                      <div className="w-20 h-20 border-4 border-purple-500/30 rounded-full" />
                      <div className="absolute top-0 left-0 w-20 h-20 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                    <span className="text-white text-lg font-medium animate-pulse">
                      {streamUrl?.toLowerCase().includes(".mkv")
                        ? "Loading MKV file..."
                        : "Loading stream..."}
                    </span>
                  </div>
                </div>
              )}

              {/* MKV Status with fade animation */}
              <AnimatePresence>
                {mkvWarning && !error && (
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    className={`absolute top-16 left-4 z-40 ${mkvWarning.includes("successfully") ? "bg-green-600/90" : "bg-amber-600/90"} text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 shadow-lg backdrop-blur-sm`}
                    exit={{ opacity: 0, y: -10 }}
                    initial={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <i
                      className={`fas ${mkvWarning.includes("successfully") ? "fa-check-circle" : "fa-exclamation-triangle"}`}
                    />
                    <span>{mkvWarning}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Error message */}
              {error && (
                <div className="absolute inset-0 flex items-center justify-center z-30 bg-black/50">
                  <div className="flex flex-col items-center gap-4 text-white p-8 max-w-md text-center">
                    <svg
                      className="w-16 h-16 text-red-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                      />
                    </svg>
                    <span className="text-lg">{error}</span>
                    <button
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors font-medium"
                      onClick={onClose}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}

              {/* Container for Plyr to manipulate - prevents React DOM conflicts */}
              <div className="plyr-container w-full h-full">
                <video
                  playsInline
                  className="w-full h-full"
                  ref={videoRef}
                  style={{ backgroundColor: "#000" }}
                >
                  Your browser doesn't support HTML video.
                </video>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Subtitles Modal */}
        <SubtitlesModal
          hasNativeSubtitles={hasNativeSubtitles}
          isOpen={showSubtitlesModal}
          loading={subtitlesLoading}
          nativeSubtitlesEnabled={nativeSubtitlesEnabled}
          selectedSubtitle={selectedSubtitle}
          subtitles={subtitles}
          onClose={() => setShowSubtitlesModal(false)}
          onSelectSubtitle={handleSubtitleSelect}
          onToggleNativeSubtitles={toggleNativeSubtitles}
        />
      </motion.div>
    </AnimatePresence>
  );
};

export default PlyrVideoPlayer;
